import { BestType } from '@diritto/common/best/constants';
import { CategoryNavigationMenu } from '@diritto/common/navigation/constant';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import {
  ContentListType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import ContentFavoriteAndEpisodeNotiButton from 'app-components/contents/ContentFavoriteAndEpisodeNotiButton';
import { ContentItemMeta } from 'app-components/contents/ContentItemMeta';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ArrowRoundIcon } from 'icons/ArrowRoundIcon';
import React, { useCallback, useMemo, useState } from 'react';
import { useContentReactionStore } from 'stores/contents/ContentReactionStore';
import { twMerge } from 'tailwind-merge';
import { Link } from 'ui-components/common/Link';
import { ContentBadgeGroup } from 'ui-components/contents/ContentBadgeGroup';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { IconCountType } from 'ui-components/contents/IconCount';
import { getCategoryNavMenuFromCategoryType, getCategoryTypeFromId } from 'utils/category';
import { getIconCountMap } from 'utils/content';

interface RankingContentItemProps {
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  selectedCategoryId?: number;
  listItemIndex?: number;
  bestType: BestType;
  hideDesc?: boolean;
  className?: string;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Best,
};

export const RankingContentItem = ({
  content,
  profile,
  selectedCategoryId,
  bestType,
  listItemIndex = 0,
  className,
  hideDesc = false,
}: RankingContentItemProps) => {
  const { favoriteCount, newEpisodeNotiCount } = content.counts;
  const { tracker } = useTrackingState();
  const [isExpanded, setIsExpanded] = useState(false);
  const { store } = useContentReactionStore();

  const iconCountMap = useMemo(
    () =>
      getIconCountMap([IconCountType.View, IconCountType.Recommend, IconCountType.Favorite], {
        viewCount: content.counts.viewCount,
        recommendCount: content.counts.recommendCount,
        favoriteCount: content.counts.favoriteCount,
      }),
    [content],
  );

  const handleClick = useCallback(() => {
    const categoryType = getCategoryTypeFromId(selectedCategoryId as number);
    const categoryNavMenu = getCategoryNavMenuFromCategoryType(categoryType);

    tracker.track(TrackingEventType.SelectContentListItem, {
      listType: ContentListType.Best,
      listItemIndex,
      contentId: content.id,
      params: {
        category: categoryNavMenu || CategoryNavigationMenu.All,
        bestType,
      },
      trackerExtraInfo,
    });
  }, [bestType, content.id, selectedCategoryId, listItemIndex, tracker]);

  const handleExtend = useCallback(async () => {
    if (!isExpanded && !store.state.contents[content.id]) {
      await store.updateContentFavoriteCount(content.id, favoriteCount);
      store.updateContentNotiCount(content.id, newEpisodeNotiCount);
    }

    setIsExpanded(!isExpanded);
  }, [content.id, favoriteCount, isExpanded, newEpisodeNotiCount, store]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={twMerge('flex flex-col w-full h-full space-y-1.5', className)}
      onClick={handleClick}
      onKeyDown={e => e.key === 'Enter' && handleClick()}>
      <div className="flex flex-row w-full space-x-3">
        <p className="flex flex-shrink-0 w-[33px] justify-center items-center text-sm font-medium">
          {listItemIndex + 1}
        </p>
        <Link className="flex flex-shrink-0 w-[60px] h-[90px] overflow-hidden" href={`/contents/${content.id}`}>
          <div className="relative w-[100px] h-[150px]">
            <ContentCover className="scale-[60%] origin-top-left" content={content}>
              <ContentBadgeGroup content={content} />
            </ContentCover>
          </div>
        </Link>
        <div className="flex flex-row items-center w-full">
          <ContentItemMeta
            content={content}
            profile={profile}
            iconCounts={iconCountMap}
            episodeCount={content.counts.episodeCount}
            showCategoryType
            showEpisodeCount
            href={`/contents/${content.id}`}
          />
        </div>
      </div>
      {!hideDesc && (
        <div className="flex flex-row w-full space-x-3">
          <p className="flex flex-shrink-0 w-[33px] justify-center items-center text-sm font-medium" />
          <div
            role="button"
            tabIndex={0}
            className="flex flex-row w-full p-2 bg-gray-100 relative rounded-[4px] space-x-[6px]"
            onClick={handleExtend}
            onKeyDown={e => e.key === 'Enter' && handleExtend()}>
            <div className="flex flex-col w-full text-xxxs font-regular text-gray-700">
              <p className={`${isExpanded ? 'line-clamp-5' : 'line-clamp-2'} transition-all text-desc`}>
                {content.description}
              </p>
              {isExpanded && (
                <div className="flex flex-row space-x-2 scale-66 origin-left mt-1.5 h-6">
                  <ContentFavoriteAndEpisodeNotiButton
                    color={Color.Gray400}
                    contentId={content.id}
                    trackerExtraInfo={trackerExtraInfo}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-shrink-0 h-full items-end">
              <ArrowRoundIcon direction={isExpanded ? 'up' : 'down'} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
